import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { clearAllStoredComposers } from "@/lib/generateComposerStorage";
import {
  isTransientAxiosError,
  isAuthFailureStatus,
  TRANSIENT_STATUS,
  RETRY_DELAY_MS,
} from "@/lib/authTransient";

const AuthContext = createContext(null);

// -----------------------------------------------------------------------------
// Auth check with transient-failure tolerance
// -----------------------------------------------------------------------------
// Historical behavior: ANY error from GET /api/auth/me (network blip, 500,
// timeout, CORS hiccup) called setUser(null), which the router interprets as
// "logged out" and redirects to sign-in. Users refreshing during a Mongo
// AutoReconnect blip or a transient upstream 502 were falsely signed out.
//
// New rules (production-stability fix):
//   - Only an EXPLICIT 401 or 403 response clears the user.
//   - Network errors and 500/502/503/504 do NOT clear the user; instead we
//     surface a lightweight "connectionError" flag so the UI can show a
//     temporary banner.
//   - Transient failures trigger exactly ONE retry after a short delay.
//   - The retry itself has no further retries — no infinite loops.
// -----------------------------------------------------------------------------

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // ``connectionError`` is a lightweight signal that the auth check failed
  // for a transient reason. Consumers may render a small banner. It is
  // intentionally separate from ``user`` so we never conflate "connectivity
  // is flaky" with "you are signed out".
  const [connectionError, setConnectionError] = useState(false);

  const checkAuth = useCallback(async () => {
    // Try once; on transient failure retry once; then decide what to do.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setConnectionError(false);
        setLoading(false);
        return;
      } catch (err) {
        if (isAuthFailureStatus(err)) {
          // Explicit 401 / 403 — session really is invalid. Sign out.
          setUser(null);
          setConnectionError(false);
          setLoading(false);
          // Redacted diagnostic log — no tokens, no PII.
          // eslint-disable-next-line no-console
          console.warn(
            "[auth] /auth/me returned",
            err?.response?.status,
            "— clearing session",
          );
          return;
        }
        if (!isTransientAxiosError(err)) {
          // Unexpected non-transient, non-auth error (e.g. 400 / 422).
          // Do not sign out; just show the banner and stop retrying.
          // eslint-disable-next-line no-console
          console.warn(
            "[auth] /auth/me unexpected status",
            err?.response?.status,
          );
          setConnectionError(true);
          setLoading(false);
          return;
        }
        // Transient — retry once.
        // eslint-disable-next-line no-console
        console.warn(
          "[auth] /auth/me transient failure (status=",
          err?.response?.status ?? "network",
          ") — attempt",
          attempt + 1,
        );
        if (attempt === 0) {
          await sleep(RETRY_DELAY_MS);
          continue;
        }
        // Retry also failed. KEEP existing user/session state as-is, just
        // set the banner. Never bounce the user to sign-in for a transient.
        setConnectionError(true);
        setLoading(false);
        return;
      }
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    try {
      localStorage.removeItem("sg_session_token");
    } catch (_e) {
      /* ignore */
    }
    // Session-scoped Generate Post composer state (unfinished typing per
    // workspace) must not survive across logins. Only clears the keys
    // owned by this feature — all other sessionStorage is untouched.
    clearAllStoredComposers();
    setUser(null);
    setConnectionError(false);
  }, []);

  const value = {
    user,
    setUser,
    loading,
    connectionError,
    refresh: checkAuth,
    logout,
    isAdmin: !!user?.is_admin,
    isAdminEligible: !!user?.is_admin_eligible,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

// Exported for unit tests only — do not import from application code.
export const __testables__ = {
  isTransientAxiosError,
  isAuthFailureStatus,
  TRANSIENT_STATUS,
  RETRY_DELAY_MS,
};
